import { usePatientDetailContext, useDrawerContext } from 'context/hooks';
import { useParams } from 'react-router-dom';
import { DistributionValues } from 'models/charts';
import { BloodPressureItem } from 'models/bloodpressure';
import { t } from 'i18next';
import { useCallback, useMemo } from 'react';
import { getBloodPressureClassificationColor } from 'utils/measurements/bloodpressure';
import { Symptom } from 'models/generic';
import { renderBloodPressureDetails } from 'pages/Main/Patients/Details/Overview/BloodPressureOverview/BloodPressureDetails.helpers';
import { useGetBloodPressureMeasurements } from './useGetBloodPressureMeasurements';
import { usePatient } from '../useUser';
import { useGetBloodPressureClassification } from './useGetBloodPressureClassification';

export const useBloodPressureOverview = () => {
  const { startDate, endDate, symptoms } = usePatientDetailContext();
  const { patientId } = useParams<{ patientId: string }>();
  const { data: patientData } = usePatient({ patientId });
  const { openDrawer } = useDrawerContext();
  const groupIds = patientData?.bloodPressureEnabledGroupsIds;

  const { data: bloodPressureClassification } = useGetBloodPressureClassification();

  const { data, isLoading } = useGetBloodPressureMeasurements({
    userId: patientId,
    startDate: new Date(startDate).getTime(),
    endDate: new Date(endDate).getTime(),
    symptoms,
    groupIds,
    classificationGuidelineId: bloodPressureClassification?.id,
  });

  const mappedData: BloodPressureItem[] = useMemo(() => {
    if (!data) return [];

    return data.map((item) => {
      const itemClassification = item.data?.classifications.find(
        (c) => c.guidelineId === bloodPressureClassification?.id,
      );
      const itemSymptoms = item.data?.context?.symptoms || [];

      return {
        id: item.id as string,
        bloodPressure: `${item.data?.systolic || 0}/${item.data?.diastolic || 0}`,
        pulsePressure: item.data?.pulsePressure || 0,
        date: new Date(item.data ? item.data.measurementTimestamp : new Date()).getTime(),
        symptoms: itemSymptoms.includes(Symptom.NoSymptoms) ? [] : itemSymptoms,
        commentCount: item?.commentCount,
        attachment: item.data?.attachmentUrl || undefined,
        type: item.data?.type,
        classification: {
          color: getBloodPressureClassificationColor(itemClassification?.color),
          label: itemClassification?.label,
        },
      };
    });
  }, [data, bloodPressureClassification]);

  const distributionValues: DistributionValues<string> = {
    total: data?.length || 0,
    values:
      bloodPressureClassification?.data?.classifications.map((classification) => {
        return {
          label: classification.label,
          total: mappedData.filter((item) => item.classification.label === classification.label).length,
          color: getBloodPressureClassificationColor(classification?.color),
          name: t(`severity_${classification.label}`),
        };
      }) ?? [],
  };

  const getItemById = useCallback(
    (id: string) => {
      return mappedData.find((item) => item.id === id);
    },
    [mappedData],
  );

  const getItemByDate = useCallback(
    (date: number) => {
      return mappedData.find((item) => item.date === date);
    },
    [mappedData],
  );

  const handleSelected = (item?: BloodPressureItem) => {
    if (item) {
      openDrawer({
        title: t('details_title'),
        content: renderBloodPressureDetails(item),
        size: 'md',
        detailId: item.id,
      });
    }
  };

  const isEnabled = (groupIds && groupIds.length > 0) || false;

  return {
    isLoading,
    data: mappedData,
    isEnabled,
    getItemById,
    getItemByDate,
    distributionValues,
    handleSelected,
  };
};
